import { DOCUMENT } from '@angular/common';
import { Injectable, EventEmitter, Injector, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { PageType } from '../../analytics/GA4/models/context.model';
import { LoginPreselectedFields } from '../models/transversal.models';

// @api
import { ProductsService } from '@api/catalog/products/products.service';
import { Order } from '@api/order/order.models';
// @core
import { CommonWindow } from '@core/domain/models/common-window.model';
import { Resolutions } from '@core/domain/state/core.state';
import { SetPage } from '@core/infrastructure/state/core-commands.actions';
import { getResolution } from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/services/storage/storage.service';
// @pages
import { PublicUser } from '@features/user';
import { LoginSuccessAction } from '@pages/authentication/store/authentication.actions';
import { LoadCategoryPage } from '@pages/category/store/category-page.actions';
import { AnalyticsEvent } from '@shared/analytics/store/analytics.actions';
import { OpenCategoriesInHeader } from '@shared/menu-categories/store/menu-categories.actions';
import { GoTo, GoToSection } from '@shared/navigation/store/navigation.actions';
import { NavigationService } from '@shared/services/navigation/navigation.service';
import { UpdateShopCart } from '@shared/shopcart/store/shopcart.actions';
import { LogoutAction, getUserSelector } from '@shared/user/store';
import { OpenModal } from '@ui/dialog/store/modal.actions';

@Injectable({ providedIn: 'root' })
export class TransversalEventsService {
    public nameEventLegacy = 'legacy-event';

    public user: PublicUser | null = null;

    public cmsEvent: EventEmitter<string>;

    private readonly resolution$: Observable<Resolutions> = this.store.select(getResolution);

    constructor(
        private readonly store: Store,
        private readonly productsService: ProductsService,
        private readonly injector: Injector,
        private readonly activatedRoute: ActivatedRoute,
        private readonly storageService: CoreStorageService,
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) {
        // aqui añadir un evento

        this.document.dispatchEvent(new CustomEvent('init-transversal-events'));
        this.document.addEventListener('legacy-event-apollo', (param: any) => {
            if (param.detail?.extra) {
                const paramAngularJs = param.detail.extra;
                switch (paramAngularJs.typeEvent) {
                    case 'category':
                        this.store.dispatch(LoadCategoryPage({ categoryId: paramAngularJs.categoryId }));
                        break;
                    case 'login-success':
                        this.loginSuccess(paramAngularJs.data);
                        break;
                    case 'menu':
                        this.store.dispatch(OpenCategoriesInHeader({ categoryId: paramAngularJs.categoryId }));
                        break;
                    case 'buylater-add-item':
                    case 'update-shop-cart':
                    case 'mini-wish':
                        // console.warn('DEPRECATED');
                        break;
                    case 'update-cart-legacy':
                        this.store.dispatch(UpdateShopCart());
                        break;
                    case 'return':
                        this.showReturn(param);
                        break;
                    case 'gift-ticket-download':
                        this.store.dispatch(OpenModal({ modalId: 'gift-ticket-download', modalData: paramAngularJs }));
                        break;
                    case 'change':
                        this.showChange(param);
                        break;
                    case 'logout':
                        this.logout();
                        break;
                    case 'change-password':
                        this.changePassword(paramAngularJs.hash);
                        break;
                    case 'verification-phone-legacy':
                        this.store.dispatch(AnalyticsEvent({
                            eventData: {
                                cf: 'perfil_usuario',
                                category: 'datos_personales',
                                action: 'verificar_cuenta',
                            },
                        }));
                        this.store.dispatch(OpenModal({ modalId: 'phone-verification', modalData: { phone: paramAngularJs.phone } }));
                        break;
                    case 'open-user-id':
                        this.store.dispatch(OpenModal({ modalId: 'user-id' }));
                        break;
                    case 'set-page':
                        this.setPage(paramAngularJs.page);
                        break;
                    default:
                        break;
                }
            }
        });

        this.store.select(getUserSelector).pipe(filter(user => !!user)).subscribe((user) => {
            if (user) {
                this.user = user;
            }
        });

        this.cmsEvent = new EventEmitter<string>();
    }

    public init(): void {
        // eslint-disable-next-line no-console
        console.log('Init transversal events');
    }

    public loginSuccess(user) {
        this.store.dispatch(LoginSuccessAction({ user }));
    }

    public logout() {
        this.store.dispatch(LogoutAction());
    }

    public changePassword(hash: string) {
        const navigationService = this.injector.get(NavigationService);
        navigationService.recoveryPasswordUrl$.subscribe(url=> {
            let urlred: string = url + '?hash=' + encodeURIComponent(hash);
            this.store.dispatch(GoTo({ url: urlred }));
        });
    }

    public setPage(page: PageType): void {
        this.store.dispatch(SetPage({ page }));
    }

    public openOrderRefundForm(order: Partial<Order>, typeLegacy: string) {
        this.dispatchEvent(this.nameEventLegacy, { type: 'openRefundForm', order: order, typeLegacy });
    }

    public openOrderReturnForm(order: Partial<Order>, typeLegacy: string) {
        this.dispatchEvent(this.nameEventLegacy, { type: 'openReturnForm', order: order, typeLegacy });
    }

    public openLogin(preselectedFields: LoginPreselectedFields = {}) {
        if (!!this.window.inditex.hybridPage) {
            this.storageService.setValue('RELOAD_HYBRIDPAGE', window.location.pathname, 'SESSION', 'TECHNICAL');
        } else {
            this.storageService.removeValue('RELOAD_HYBRIDPAGE');
        }
        this.store.dispatch(GoToSection({ url: 'userLoginApolo', state: preselectedFields }));
    }

    public openChangePage(data) {
        this.dispatchEvent(this.nameEventLegacy, { type: 'changeSection', data });
    }

    public openMyOrders() {
        this.dispatchEvent(this.nameEventLegacy, { type: 'myOrders' });
    }

    public checkArray(p) {
        return p?.bundleProductSummaries?.length > 0;
    }

    public showReturn(data): void {
        const navigationService = this.injector.get(NavigationService);
        navigationService.goToReturns(data?.detail?.order);
    }

    public showChange(data): void {
        const navigationService = this.injector.get(NavigationService);
        navigationService.goToChangeReturnSelector(data?.detail?.order);
    }

    private dispatchEvent(name, extra) {
        const options = {
            bubbles: true,
            composed: true,
            detail: {
                extra,
            },
        };
        const exampleEvent = new CustomEvent(name, options);
        this.document.dispatchEvent(exampleEvent);
    }
}
